// src/follower/components/FollowerAccountSummary.jsx
// Fetches the follower's Alpaca account summary from GET /follower/account-summary
// and renders 4 stat chips + a 30-day equity curve.
//
// Props: none — reads token from sessionStorage, calls Lambda directly.
// Placement: between AccountStatusBar and GuardrailsPanel in myTrades.js.

import React, { useState, useEffect, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import axios from 'axios';
import config from '../../config';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function lambdaHeaders() {
    const token = sessionStorage.getItem('token');
    return {
        'x-api-key':     config.API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type':  'application/json',
    };
}

function formatDollar(val) {
    if (val == null) return '--';
    const abs = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return val < 0 ? `-$${abs}` : `$${abs}`;
}

function formatPnL(val) {
    if (val == null) return '--';
    const abs = Math.abs(val).toFixed(2);
    return val >= 0 ? `+$${abs}` : `-$${abs}`;
}

function formatPct(val) {
    if (val == null) return '--';
    return val >= 0 ? `+${val.toFixed(2)}%` : `${val.toFixed(2)}%`;
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, valueColor, sub }) {
    return (
        <div style={{
            flex:         1,
            minWidth:     120,
            background:   C.bg2,
            border:       `1px solid ${C.border}`,
            borderRadius: 8,
            padding:      "14px 18px",
        }}>
            <div style={{
                fontFamily:    FONT,
                fontSize:      8,
                color:         C.meta,
                letterSpacing: "1px",
                textTransform: "uppercase",
                marginBottom:  6,
            }}>
                {label}
            </div>
            <div style={{
                fontFamily:    FONT,
                fontSize:      18,
                fontWeight:    500,
                color:         valueColor || C.text,
                letterSpacing: ".3px",
                lineHeight:    1.2,
            }}>
                {value}
            </div>
            {sub && (
            <div style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    color:         C.meta,
                    marginTop:     4,
                    letterSpacing: ".3px",
                }}>
                {sub}
            </div>
                )}
        </div>
    );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function Skeleton({ height = 16, width = "100%", style = {} }) {
    return (
        <div style={{
            height,
            width,
            borderRadius: 4,
            background:   C.border,
            opacity:      0.5,
            ...style,
        }} />
    );
}

function LoadingState() {
    return (
        <div style={{
            background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 10,
            padding:      "16px 20px",
        }}>
            {/* Header skeleton */}
            <Skeleton height={10} width={140} style={{ marginBottom: 16 }} />

            {/* Chip row skeleton */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                {[1, 2, 3, 4].map(i => (
                <div key={i} style={{
                        flex: 1, minWidth: 120,
                        background: C.bg2, border: `1px solid ${C.border}`,
                        borderRadius: 8, padding: "14px 18px",
                    }}>
                    <Skeleton height={8} width={60} style={{ marginBottom: 10 }} />
                    <Skeleton height={20} width={90} />
                </div>
                    ))}
            </div>

            {/* Chart skeleton */}
            <Skeleton height={90} />
        </div>
    );
}

// ─── Error state ──────────────────────────────────────────────────────────────
function ErrorState({ message, onRetry }) {
    return (
        <div style={{
            background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 10,
            padding:      "16px 20px",
            display:      "flex",
            alignItems:   "center",
            gap:          12,
        }}>
            <div style={{
                width: 6, height: 6, borderRadius: "50%",
                background: C.meta, flexShrink: 0,
            }} />
            <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: ".3px", flex: 1 }}>
                {message || "Account data unavailable"}
            </span>
            {onRetry && (
            <button
                onClick={onRetry}
                style={{
                        fontFamily: FONT, fontSize: 8, letterSpacing: ".8px",
                        color: C.meta, background: "transparent",
                        border: `1px solid ${C.border}`, borderRadius: 3,
                        padding: "3px 8px", cursor: "pointer",
                    }}
            >
                RETRY
            </button>
                )}
        </div>
    );
}

// ─── Equity curve chart ───────────────────────────────────────────────────────
function EquityCurve({ history }) {
    const filtered = (history || []).filter(h => h.equity != null && h.equity > 0);
    if (filtered.length < 2) return null;

    const firstEquity = filtered[0]?.equity ?? 0;
    const lastEquity  = filtered[filtered.length - 1]?.equity ?? 0;
    const isUp        = lastEquity >= firstEquity;
    const lineColor   = isUp ? C.green : C.red;

    const series = [{
        name: "Portfolio Value",
        data: filtered.map(h => ({
            x: new Date(h.date).getTime(),
            y: h.equity,
        })),
    }];

    const options = {
        chart: {
            type:       "area",
            height:     100,
            sparkline:  { enabled: false },
            background: "transparent",
            toolbar:    { show: false },
            zoom:       { enabled: false },
            animations: { enabled: false },
        },
        stroke: {
            curve: "smooth",
            width: 2,
            colors: [lineColor],
        },
        fill: {
            type:     "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom:    0.25,
                opacityTo:      0.02,
                stops:          [0, 100],
                colorStops: [{
                    offset:  0,
                    color:   lineColor,
                    opacity: 0.25,
                }, {
                    offset:  100,
                    color:   lineColor,
                    opacity: 0.02,
                }],
            },
        },
        xaxis: {
            type:   "datetime",
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks:  { show: false },
        },
        yaxis: {
            show: false,
        },
        grid: {
            show:        true,
            borderColor: C.border,
            strokeDashArray: 3,
            yaxis: { lines: { show: true  } },
            xaxis: { lines: { show: false } },
            padding: { top: 4, right: 8, bottom: 4, left: 8 },
        },
        tooltip: {
            theme: "dark",
            style: { fontFamily: FONT, fontSize: "10px" },
            x: { format: "MMM dd" },
            y: {
                formatter: (val) => `$${val?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
        },
        markers: { size: 0 },
        dataLabels: { enabled: false },
        theme: { mode: "dark" },
    };

    return (
        <div style={{ marginTop: 12 }}>
            <ReactApexChart
                type="area"
                series={series}
                options={options}
                height={100}
            />
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function FollowerAccountSummary() {
    const [summary,   setSummary]   = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);
    const [fetchedAt, setFetchedAt] = useState(null); // timestamp of last successful fetch

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(
                config.API_BASE_URL + '/follower/account-summary',
                { headers: lambdaHeaders() }
            );
            setSummary(res.data);
            setFetchedAt(Date.now());
        } catch (err) {
            const msg = err.response?.data?.message || 'Unable to load account data.';
            setError(msg);
            console.error('[FollowerAccountSummary] fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    // ── Render states ─────────────────────────────────────────────────────────
    if (loading) return <LoadingState />;
    if (error)   return <ErrorState message={error} onRetry={fetchSummary} />;
    if (!summary) return null;

    const pnlColor = summary.today_pnl > 0 ? C.green
        : summary.today_pnl < 0 ? C.red
        : C.meta;

    const modeLabel = summary.trading_mode === 'live' ? 'Live account' : 'Paper account';

    return (
        <div style={{
            background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 10,
            padding:      "16px 20px",
        }}>
            {/* Header */}
            <div style={{
                display:       "flex",
                alignItems:    "center",
                gap:           8,
                marginBottom:  14,
            }}>
                <span style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    color:         C.meta,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                }}>
                    Account Overview
                </span>
                <span style={{
                    fontFamily:    FONT,
                    fontSize:      8,
                    color:         C.meta,
                    background:    C.bg2,
                    border:        `1px solid ${C.border}`,
                    borderRadius:  3,
                    padding:       "1px 6px",
                    letterSpacing: ".5px",
                }}>
                    {modeLabel}
                </span>
                {fetchedAt && (
                <span style={{
                        fontFamily:    FONT,
                        fontSize:      8,
                        color:         C.meta,
                        letterSpacing: ".3px",
                    }}>
                        as of {new Date(fetchedAt).toLocaleTimeString("en-US", {
                    hour:   "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "America/New_York",
                    })} ET
                    </span>
                    )}
                {/* Refresh button — pushed to far right */}
                <button
                    onClick={fetchSummary}
                    disabled={loading}
                    style={{
                        marginLeft:    "auto",
                        fontFamily:    FONT,
                        fontSize:      8,
                        letterSpacing: ".8px",
                        color:         loading ? C.meta : C.cyan,
                        background:    "transparent",
                        border:        `1px solid ${loading ? C.border : C.cyan + "40"}`,
                        borderRadius:  3,
                        padding:       "2px 8px",
                        cursor:        loading ? "default" : "pointer",
                        transition:    "color .15s, border-color .15s",
                        opacity:       loading ? 0.5 : 1,
                    }}
                >
                    {loading ? "LOADING..." : "↻ REFRESH"}
                </button>
            </div>

            {/* Stat chips */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <StatChip
                    label="Portfolio Value"
                    value={formatDollar(summary.equity)}
                    valueColor={C.text}
                />
                <StatChip
                    label="Today's P&L"
                    value={formatPnL(summary.today_pnl)}
                    valueColor={pnlColor}
                    sub={formatPct(summary.today_pnl_pct)}
                />
                <StatChip
                    label="Cash"
                    value={formatDollar(summary.cash)}
                    valueColor={C.body}
                />
                <StatChip
                    label="Buying Power"
                    value={formatDollar(summary.buying_power)}
                    valueColor={C.cyan}
                />
            </div>

            {/* Equity curve */}
            <EquityCurve history={summary.portfolio_history} />

            {/* Footer — disclaimer */}
            <div style={{
                marginTop:      8,
                display:        "flex",
                justifyContent: "space-between",
                alignItems:     "center",
                flexWrap:       "wrap",
                gap:            6,
            }}>
                <span style={{
                    fontFamily:    FONT,
                    fontSize:      8,
                    color:         C.meta,
                    letterSpacing: ".4px",
                    opacity:       0.7,
                }}>
                    Portfolio data pulled from Alpaca · may lag live session P&L by a few minutes
                </span>
                {summary.portfolio_history?.length > 0 && (
                    <span style={{
                    fontFamily:    FONT,
                    fontSize:      8,
                    color:         C.meta,
                    letterSpacing: ".5px",
                    }}>
                    30-day equity curve · Alpaca {summary.trading_mode}
                    </span>
                    )}
            </div>
        </div>
    );
}